// Scroll anchoring for data updates.
//
// When new results arrive, content above the reader can grow or shrink (a banner appears, a card gains a line, the
// first results replace the early-vote hero). Chrome/Firefox/Edge keep the reader in place with native CSS scroll
// anchoring; iPhone Safari has none, so the page would jump. holdScroll() is called right before an update applies:
// it remembers an element near the top of the viewport (and its ancestors, as fallbacks if it is replaced) with its
// viewport offset, then for the next few seconds scrolls the window back by however far that element moved — checked
// right after Svelte applies the update (same task, so no frame is painted in between), in a ResizeObserver callback (after layout, before paint, even when the DOM changed inside another animation-frame
// callback, e.g. the replay player's) and on every animation frame (changes that resize nothing observed). Because it measures where the element actually is, a browser that already
// anchored natively leaves nothing to correct (no double correction).
//
// Never fight the reader: a session ends as soon as they scroll, touch, click or press a key (outside text fields),
// and doesn't start while they are scrolling (e.g. iOS momentum) or are at the very top of the page.

import { tick } from 'svelte';

const HOLD_MS = 3000;

interface Session {
  chain: Element[];
  base: number[];
  until: number;
  raf: number;
  ro: ResizeObserver | null;
}
let session: Session | null = null;
/** scrollY the last time we scrolled (scroll events at any other position are the reader's / the browser's) */
let ownY = -1;
let lastExternalScroll = 0;
let wired = false;

function wire() {
  if (wired || typeof window === 'undefined') return;
  wired = true;
  const stop = () => cancelScrollHold();
  for (const ev of ['wheel', 'touchstart', 'pointerdown'] as const) window.addEventListener(ev, stop, { capture: true, passive: true });
  window.addEventListener('keydown', (e) => {
    const t = e.target as HTMLElement | null;
    if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return; // typing doesn't scroll the page
    stop();
  }, { capture: true });
  window.addEventListener('scroll', () => {
    if (Math.abs(window.scrollY - ownY) <= 1) return; // our own correction
    lastExternalScroll = performance.now();
    stop();
  }, { passive: true });
}

function headerBottom(): number {
  const h = document.querySelector('header.top');
  return h ? Math.max(0, h.getBoundingClientRect().bottom) : 0;
}

/** Nearest ancestor (or self) that is sticky/fixed — such elements don't move with the page. */
function pinned(el: Element): boolean {
  for (let a: Element | null = el; a && a !== document.body; a = a.parentElement) {
    const p = getComputedStyle(a).position;
    if (p === 'sticky' || p === 'fixed') return true;
  }
  return false;
}

/** Inner scroll box around `el` (its own scrolling isn't a page shift, so the box itself is the anchor). */
function innerScroller(el: Element): Element | null {
  let box: Element | null = null;
  for (let a: Element | null = el.parentElement; a && a !== document.body; a = a.parentElement) {
    const o = getComputedStyle(a).overflowY;
    if ((o === 'auto' || o === 'scroll') && a.scrollHeight > a.clientHeight) box = a;
  }
  return box;
}

/**
 * The element the reader is looking at: scanning down from just below the sticky bar, the deepest element whose top
 * edge is on screen (like native scroll anchoring, which prefers the first fully visible box — a tall card that
 * starts above the screen would keep its top fixed while its lower part, the part being read, moves). Returned with
 * its ancestors, which serve as fallbacks if it is replaced.
 */
function pick(): Element[] | null {
  const top = headerBottom();
  const limit = top + Math.max(120, (window.innerHeight - top) * 0.6);
  for (let y = top + 8; y < limit; y += 24) {
    for (const fx of [0.5, 0.3, 0.7, 0.15, 0.85]) {
      const hit = document.elementFromPoint(window.innerWidth * fx, y);
      if (!hit || hit === document.body || hit === document.documentElement || pinned(hit)) continue;
      const el = innerScroller(hit) ?? (hit.getBoundingClientRect().top >= top - 1 ? hit : null);
      if (!el) continue; // starts above the screen: look a little lower
      const chain: Element[] = [];
      for (let a: Element | null = el; a && a !== document.body; a = a.parentElement) chain.push(a);
      return chain;
    }
  }
  return null;
}

/** Scroll back by however far the anchor (or, if it was replaced, its nearest surviving ancestor) moved. */
function correct(s: Session): boolean {
  let i = 0;
  while (i < s.chain.length && !s.chain[i]!.isConnected) i++;
  if (i >= s.chain.length) return false;
  const d = s.chain[i]!.getBoundingClientRect().top - s.base[i]!;
  if (Math.abs(d) >= 0.5) {
    window.scrollBy(0, d);
    ownY = window.scrollY;
  }
  // re-baseline the live part of the chain (after the correction) so a fallback ancestor starts from here
  if (i > 0) { s.chain.splice(0, i); s.base.splice(0, i); }
  for (let j = 0; j < s.chain.length; j++) s.base[j] = s.chain[j]!.getBoundingClientRect().top;
  return true;
}

function frame() {
  const s = session;
  if (!s) return;
  if (!correct(s) || performance.now() > s.until) { cancelScrollHold(); return; }
  s.raf = requestAnimationFrame(frame);
}

/** Keep what the reader is looking at in place while a data update settles (call right before it applies). */
export function holdScroll(ms = HOLD_MS) {
  if (typeof window === 'undefined') return;
  wire();
  if (session) {
    session.until = Math.max(session.until, performance.now() + ms);
    afterUpdate(session);
    return;
  }
  if (window.scrollY <= 0 || performance.now() - lastExternalScroll < 250) return;
  const chain = pick();
  if (!chain) return;
  ownY = window.scrollY;
  const s: Session = { chain, base: chain.map((e) => e.getBoundingClientRect().top), until: performance.now() + ms, raf: 0, ro: null };
  session = s;
  if (typeof ResizeObserver !== 'undefined') {
    s.ro = new ResizeObserver(() => { if (session === s && !correct(s)) cancelScrollHold(); });
    s.ro.observe(document.body);
    for (const el of chain) s.ro.observe(el);
  }
  s.raf = requestAnimationFrame(frame);
  afterUpdate(s);
}

/** Correct as soon as the state change made right after holdScroll() is in the DOM (before anything is painted). */
function afterUpdate(s: Session) {
  void tick().then(() => { if (session === s && !correct(s)) cancelScrollHold(); });
}

export function cancelScrollHold() {
  if (!session) return;
  cancelAnimationFrame(session.raf);
  session.ro?.disconnect();
  session = null;
}
